import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection,
  collectionGroup,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { SubscriptionStatus, type Currency } from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { formatCurrencyCents, formatUsdCents } from './formatters';
import {
  parseSubscriptionDocs,
  rankTopDirectRevenue,
  reportSubscriptionDrops,
  type SubscriptionDrop,
} from './subscriptionRevenue';

const TAG = 'admin/finance/TopRevenueAccounts';
const ROW_LIMIT = 10;

type TopAccountRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  priceCents: number;
  currency: Currency;
  usdEqCents: number;
};

/**
 * Tabela "Top contas por receita" do `/dashboard/finance`.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 (rota /dashboard/finance).
 *
 * Contrato: lê todas as `tenants/{id}/billing/subscription` ACTIVE (collection
 * group `billing`), ranqueia EM MEMÓRIA pela receita direta mensal em USD eq.
 * (`rankTopDirectRevenue`) e mostra as `ROW_LIMIT` maiores, com o valor mensal
 * na moeda nativa. Nome/país vêm de `tenants/{id}`. Erro → `Alert` +
 * `reportNonFatal('admin_finance_top_accounts_failed')`; doc fora do schema →
 * `admin_subscription_doc_dropped`.
 *
 * Usado por: `FinanceDashboardPage.tsx` (web `apps/admin`, super-admin).
 *
 * Arquitetura: mesma régua do card de MRR (`isDirectPaying` + equivalente
 * mensal, #679 D15) via `subscriptionRevenue.ts` — cortesia, demo, parceiro e
 * test mode não aparecem. Valor `total_amount_cents ?? amount` (ver as Dicas
 * de `subscriptionRevenue.ts` sobre a divergência com o agregado).
 *
 * Dados: collection group `billing` com `status == ACTIVE` — igualdade única, servida pela
 * exceção de campo `billing.status` com escopo COLLECTION_GROUP (`fieldOverrides` em
 * `firebase/firestore.indexes.json`); autorizada por `match /{path=**}/billing/{billingDoc}`
 * (`isSuperAdmin()`) em `firebase/firestore.rules`. Nome/país: `tenants` com
 * `where('id', '==', tenantId)` — o doc guarda o próprio id em campo (`TenantSchema.id`), por
 * isso a query em vez de `doc()`. Campos da assinatura e exemplo em `subscriptionRevenue.ts`.
 *
 * Falhas conhecidas:
 *  - 2026-05-19 → 2026-10-03 (#795, ADM-4): `orderBy('priceCents')` sobre
 *    `billing/current`, doc órfão — tabela sempre vazia, sem erro.
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-4) passa a ler `billing/subscription`.
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 */
export function TopRevenueAccountsTable(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<
    | { kind: 'LOADING' }
    | { kind: 'ERROR'; error: string }
    | { kind: 'READY'; rows: TopAccountRow[] }
  >({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    /** Carrega, ranqueia e resolve nome/país das `ROW_LIMIT` contas (1 query por tenant). */
    async function load(): Promise<void> {
      try {
        // Bug corrigido em 2026-10-03 14:12 +0200 (#795, ADM-4): era
        // `orderBy('priceCents', 'desc')` sobre `billing/current`, doc órfão
        // desde 2026-05-19 (`e9692f87e`) — a tabela ficava sempre vazia. Sem
        // `orderBy` de propósito: a receita mensal em USD depende de ciclo e
        // moeda, que nenhum campo indexável carrega.
        const billingSnap = await getDocs(
          query(
            collectionGroup(firestore, 'billing'),
            where('status', '==', SubscriptionStatus.ACTIVE),
          ),
        );
        const drops: SubscriptionDrop[] = [];
        const usdSorted = rankTopDirectRevenue(parseSubscriptionDocs(billingSnap.docs, drops), ROW_LIMIT);
        reportSubscriptionDrops('finance:topAccounts', drops);

        const rows: TopAccountRow[] = await Promise.all(
          usdSorted.map(async (i): Promise<TopAccountRow> => {
            const tenantSnap = await getDocs(
              query(collection(firestore, 'tenants'), where('id', '==', i.tenantId), limit(1)),
            );
            const tenantDoc = tenantSnap.docs[0];
            const data = (tenantDoc?.data() ?? {}) as Record<string, unknown>;
            return {
              tenantId: i.tenantId,
              tenantName: (data.name as string | undefined) ?? i.tenantId,
              countryCode: (data.countryCode as string | undefined) ?? '',
              priceCents: i.monthlyCents,
              currency: i.currency,
              usdEqCents: i.usdEqCents,
            };
          }),
        );
        if (guard.cancelled) return;
        setState({ kind: 'READY', rows });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: query failed`, err);
        reportNonFatal('admin_finance_top_accounts_failed', serializeError(err));
        setState({ kind: 'ERROR', error: err instanceof Error ? err.message : String(err) });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, []);

  if (state.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (state.kind === 'ERROR') return <Alert type="error">{state.error}</Alert>;

  return (
    <Table<TopAccountRow>
      items={state.rows}
      trackBy="tenantId"
      variant="container"
      header={<Header variant="h3">{t('finance.tables.topAccounts.title', { count: ROW_LIMIT })}</Header>}
      empty={<Box textAlign="center" color="inherit">{t('finance.empty')}</Box>}
      columnDefinitions={[
        {
          id: 'account',
          header: t('finance.tables.topAccounts.account'),
          cell: (row): React.ReactNode => row.tenantName,
        },
        {
          id: 'country',
          header: t('finance.tables.topAccounts.country'),
          cell: (row): React.ReactNode => row.countryCode,
        },
        {
          id: 'native',
          header: t('finance.tables.topAccounts.native'),
          cell: (row): React.ReactNode => formatCurrencyCents(row.priceCents, row.currency),
        },
        {
          id: 'usdEq',
          header: t('finance.tables.topAccounts.usdEq', { defaultValue: 'USD eq.' }),
          cell: (row): React.ReactNode => formatUsdCents(row.usdEqCents),
        },
      ]}
    />
  );
}
